import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  useAsana,
  useAsanaActions,
  useAsanaProjects,
  useAsanaWorkspaces,
  useSeriesSettings,
} from '../api/queries';
import { Button } from './Button';
import { Modal } from './Overlays';
import { Picker } from './Picker';
import { Empty } from './Surfaces';
import { useNotices } from './Notices';

/**
 * Sending one commitment to Asana (PRD 51).
 *
 * ## It always asks, and it always arrives somewhere sensible
 *
 * Vladimir's rule, the same one the Slack channel follows: *"always ask same
 * logic as with slack channel, but have a settings in series for default
 * value."* So the picker opens on the series' project when the series has one,
 * on the last workspace used otherwise — and it is still a picker, because a
 * task filed silently into the wrong project is worse than one press.
 *
 * ## Connecting is per person, never per workspace
 *
 * The task is created **as whoever presses this**, with their own credential,
 * and appears in Asana as theirs — which is also the only honest thing for it
 * to be, since the commitment was theirs. Somebody who has not connected is
 * offered the way to, not an error.
 */
/**
 * ⚠ The five fields it uses, not `OpenItem`. A commitment is read on the
 * meeting it was made in as often as on the Open page, and typing this to one
 * page's shape is what kept the button off the other (Vladimir, 2026-09-23).
 */
export interface Convertible {
  id: string;
  text: string;
  dueAt: string | null;
  ownerEmail: string | null;
  seriesKey: string | null;
}

export function AsanaModal({
  item,
  onClose,
}: {
  item: Convertible;
  onClose: () => void;
}): ReactElement {
  const asana = useAsana();
  const { convert } = useAsanaActions();
  const { show } = useNotices();
  const series = useSeriesSettings(item.seriesKey);
  const connected = asana.data?.connected === true;
  const workspaces = useAsanaWorkspaces(connected);

  const [workspace, setWorkspace] = useState<string | null>(null);
  const [project, setProject] = useState<string | null>(null);
  const projects = useAsanaProjects(workspace);

  // Where it starts: the series' own default, then the last workspace this
  // person used, then simply the first they have.
  useEffect(() => {
    if (workspace) return;
    const fromSeries = series.data?.asanaWorkspaceGid ?? null;
    const chosen = fromSeries ?? asana.data?.defaultWorkspaceGid ?? workspaces.data?.[0]?.gid;
    if (!chosen) return;
    setWorkspace(chosen);
    if (fromSeries && series.data?.asanaProjectGid) setProject(series.data.asanaProjectGid);
  }, [workspace, series.data, asana.data, workspaces.data]);

  const send = () => {
    if (!workspace) return;
    convert.mutate(
      { id: item.id, workspaceGid: workspace, projectGid: project },
      {
        onSuccess: (task) => {
          onClose();
          show({
            tone: 'success',
            // Said when Asana would not take the owner: the task exists, but
            // nobody in Asana has it until somebody assigns it there.
            text: task.unassigned
              ? `In Asana now, unassigned — Asana had nobody it could give it to for ${task.unassigned}. Assign it there.`
              : 'In Asana now, and closed here.',
            action: { label: 'Open the task', onSelect: () => window.open(task.url, '_blank') },
          });
        },
        onError: (error) => show({ tone: 'failure', text: error.message }),
      },
    );
  };

  return (
    <Modal
      title="Send to Asana"
      onClose={onClose}
      foot={
        connected ? (
          <>
            <Button variant="quiet" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!workspace}
              working={convert.isPending ? 'Sending…' : false}
              onClick={send}
            >
              Create the task
            </Button>
          </>
        ) : null
      }
    >
      {!connected ? (
        <Empty lead="Asana is not connected yet.">
          It connects per person rather than per workspace, so the task is created as you and
          appears in Asana as yours. <Link to="/settings/connected">Connect it in Settings</Link>,
          then come back.
        </Empty>
      ) : (
        <div className="stack" style={{ gap: 14 }}>
          <div className="stack" style={{ gap: 4 }}>
            <span className="t-secondary">What becomes a task</span>
            <p className="t-ui">{item.text}</p>
            <span className="mono">
              {item.ownerEmail ? `assigned to ${item.ownerEmail}` : 'nobody to assign it to yet'}
              {item.dueAt ? ' · with its deadline' : ''} · with the words around it and the meeting
              in short
            </span>
          </div>

          <div className="setting">
            <span className="k">Workspace</span>
            <div className="v">
              <Picker
                aria-label="Workspace"
                value={workspace ?? ''}
                onChange={(gid) => {
                  setWorkspace(gid || null);
                  setProject(null);
                }}
                width={260}
                placeholder={workspaces.isPending ? 'Looking…' : 'Choose a workspace'}
                options={(workspaces.data ?? []).map((w) => ({ value: w.gid, label: w.name }))}
              />
            </div>
          </div>

          <div className="setting">
            <span className="k">Project</span>
            <div className="v">
              {/* ⚠ A real choice, not a blank. A task with no project goes to
                  the assignee's My Tasks, which is where a personal promise
                  belongs when no board owns it. */}
              <Picker
                aria-label="Project"
                value={project ?? ''}
                onChange={(gid) => setProject(gid || null)}
                width={260}
                search
                options={[
                  { value: '', label: 'My Tasks — no project' },
                  ...(projects.data ?? []).map((p) => ({ value: p.gid, label: p.name })),
                ]}
              />
              <span className="why">
                {projects.isPending && workspace ? 'looking…' : 'a series can set a default'}
              </span>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
